import { Component, HostBinding, OnDestroy, Renderer2 } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { faCircleCheck, faCircleXmark, faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { COLUMNS_HEADER_VIEW_APO } from '../../core/constants/tables.constants';
import { UploadApoService } from '../../core/services/upload-apo.service';
import { BlendersService } from '../../core/services/blenders.service';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../../core/services/auth.service';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { SpinnerComponent } from '../../components/spinner/spinner.component';
import { GenericTableComponent } from '../../components/generic-table/generic-table.component';
import { PackagingMachinesService } from '../../core/services/packaging-machines.service';
import { InputSwitchModule } from 'primeng/inputswitch';
import { TimelineComponent } from '../../components/timeline/timeline.component';
import { MultiSelectModule } from 'primeng/multiselect';
import { DatepickerComponent } from '../../components/datepicker/datepicker.component';
import { OrderType, ValueStream } from '../../core/enums/general.enum';
import { DateUtcPipe } from '../../core/pipes/date-utc.pipe';
import { CalendarService } from '../../core/services/calendar.service';
import { CalendarDay } from '../../core/interfaces/calendar.interface';
import { CameraHeatmap, CameraHeatmapSegment } from '../../core/interfaces/camera-heatmap.interface';
import {
  buildCameraHeatmapSegments,
  extractCameraHeatmapFromLogisticsResponse,
  flattenOrdersFromLogisticsResponse,
} from '../../core/utils/camera-heatmap-segments.util';
import { format, startOfMonth, endOfMonth } from 'date-fns';


@Component({
  selector: 'app-view-apo',
  standalone: true,
  imports: [CommonModule, FormsModule, FontAwesomeModule, ReactiveFormsModule, SpinnerComponent, GenericTableComponent, InputSwitchModule, TimelineComponent, MultiSelectModule, DatepickerComponent, DateUtcPipe],
  templateUrl: './view-apo.component.html',
  styleUrl: './view-apo.component.scss'
})
export class ViewApoComponent implements OnDestroy {

  @HostBinding('class.vs2-theme')
  get isVs2Theme() { return this._selectedValueStream === ValueStream.VS2; }

  COLUMNS_HEADER = COLUMNS_HEADER_VIEW_APO;
  ValueStream = ValueStream;

  faActiveCheck = faCircleCheck;
  faCloseCheck = faCircleXmark;

  private _selectedValueStream: ValueStream = ValueStream.VS1;
  get selectedValueStream() { return this._selectedValueStream; }
  set selectedValueStream(vs: ValueStream) {
    this._selectedValueStream = vs;
    this.filters.value_stream.value = vs;
    this.selectedBlenders = [];
    this.selectedPackagingMachines = [];
    this.onFilter();
    this.getFineTunningData(this.date);
    const method = vs === ValueStream.VS2 ? 'addClass' : 'removeClass';
    this.renderer[method](document.body, 'vs2-theme');
    this.renderer[method](document.documentElement, 'vs2-theme');
  }

  apoFiltered: any;
  blenders: any;
  packagingMachines: any;
  now = new Date();
  isTableView = false;
  selectedBlenders = [];
  selectedPackagingMachines = [];
  date: Date = new Date();
  calendar: any;

  blenderTasks: any = [];
  packagingTasks: any = [];

  filteredBlenderTasks: any = [];
  filteredPackagingTasks: any = [];

  cameraHeatmap: CameraHeatmap | null = null;
  cameraHeatmapSegments: CameraHeatmapSegment[] = [];

  twoWeeksMs = 14 * 24 * 60 * 60 * 1000;
  twoWeeksAgo = new Date(this.now.getTime() - this.twoWeeksMs);
  filters: any = {
    search: {
      value: '',
      filter: (obj: any): boolean => {
        return  obj.product?.id?.includes(this.filters.search.value) || obj.name.toLowerCase().includes(this.filters.search.value);
      }
    },
    dateInit: {
      value: this.twoWeeksAgo.toISOString().split('T')[0],
      filter: (obj: any): boolean => {
        return new Date(obj.fecha_inicio_blender).getTime() >= new Date(this.filters.dateInit.value).getTime();
      }
    },
    dateFin: {
      value: '',
      filter: (obj: any): boolean => {
        return new Date(obj.last_day).getTime() <= new Date(this.filters.dateFin.value).getTime();
      }
    },
    blender: {
      value: '',
      filter: (obj: any): boolean => {
        return obj.blender.description === this.filters.blender.value;
      }
    },
    packaging_machine: {
      value: '',
      filter: (obj: any): boolean => {
        return obj.packaging_machine?.id === this.filters.packaging_machine.value;
      }
    },
    value_stream: {
      value: ValueStream.VS1,
      filter: (obj: any): boolean => obj.blender?.value_stream === this.filters.value_stream.value
    },
    // load_kanbanize: {
    //   value: false,
    //   filter: (obj: any): boolean => {
    //     return !obj.errors?.template && !obj.errors?.producto && (obj.tipo === 'order' && obj['reactor_load_kanbanize']) || (obj.tipo === 'limpieza' && obj['limpieza_load_kanbanize'] && obj['reactor_load_kanbanize']);
    //   }
    // }
  };

  faSpinner = faSpinner;

  form: FormGroup = this.formBuilder.group({
    id: ['', Validators.required],
    name: [{value: '', disabled: true}, Validators.required],
    weight: [, Validators.required],
    observations: [{value: '', disabled: true}],
    production_time: [{value: '', disabled: true}, Validators.required],
    quality_time: [{value: '', disabled: true}, Validators.required],
    packaging_time: [{value: '', disabled: true}, Validators.required],
    total_time: [{value: '', disabled: true}],
    product_nature: [{value: '', disabled: true}, Validators.required],
    last_day: [{value: '', disabled: false}, Validators.required],
    loss_percent: [{value: '', disabled: true}],
    load_kanbanize: [],
    fecha_inicio_blender: ['', Validators.required],
    fecha_fin_blender: ['', Validators.required],
    envasadora_orders: this.formBuilder.array([]),
    // fecha_inicio_envasadora: ['', Validators.required],
    // fecha_fin_envasadora: ['', Validators.required],
    fecha_inicio_preparacion: [{value: '', disabled: false}, Validators.required],
    fecha_fin_preparacion: [{value: '', disabled: false}, Validators.required],
    fecha_inicio_calidad: [{value: '', disabled: false}, Validators.required],
    fecha_fin_calidad: [{value: '', disabled: false}, Validators.required],
    oee_blender: [{value: '', disabled: true}],
    oee_calidad: [{value: '', disabled: true}],
    oee_envasadora: [{value: '', disabled: true}],
    product: this.formBuilder.group({
      description: [{value: '', disabled: true}],
      id: [{value: '', disabled: true}],
      blender: [''],
      production_time: [''],
      quality_time: [''],
      start_time: [''],
      product_nature: [''],
      active: [''],
    }), 
    blender: [, Validators.required],
    material: this.formBuilder.group({
      description: [{value: '', disabled: true}],
      fert: [{value: '', disabled: true}],
      weight: [''],
      active: [''],
      packaging: this.formBuilder.group({
        id: [''],
        description: [''],
        active: [''],
        time: [''],
      }),
    }),
    packaging: [],
    // packaging_machine: [, Validators.required],
    type: ['', Validators.required],
    is_planned: [],
    fecha_carga: [''],
  });


  cleaningForm: FormGroup = this.formBuilder.group({
    id: ['', Validators.required],
    name: ['', Validators.required],
    blender: ['', Validators.required],
    fecha_inicio_blender: ['', Validators.required],
    fecha_fin_blender: ['', Validators.required],
    duration: ['', Validators.required],
    type: ['', Validators.required],
    load_kanbanize: [],
  });

  isModalActive = false;
  isModalDeleteConfirm = false;
  hasError = false;
  isLoading = false;

  private apoFile: any;

  constructor(
    private uploadApoService: UploadApoService,
    private blendersSvc: BlendersService,
    private packagingMachinesSvc: PackagingMachinesService,
    private formBuilder: FormBuilder,
    private toastr: ToastrService,
    public authService: AuthService,
    private calendarService: CalendarService,
    private renderer: Renderer2
  ) {
  }

  ngOnDestroy() {
    this.renderer.removeClass(document.body, 'vs2-theme');
    this.renderer.removeClass(document.documentElement, 'vs2-theme');
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.getBlenders();
    this.getPackagingMachines();
    this.getApoFile(this.twoWeeksAgo.toISOString().split('T')[0]);
    this.loadCalendar();
  }

  loadCalendar(): void {
    const startDate = format(startOfMonth(this.date), 'yyyy-MM-dd');
    const endDate = format(endOfMonth(this.date), 'yyyy-MM-dd');
    
    this.calendarService.getCalendar(startDate, endDate).subscribe({
      next: (data) => {
        this.calendar = data.map((day: any) => ({
          ...day,
          date: new Date(day.date)
        }));
      },
      error: (err) => {
        console.error('Error loading calendar:', err);
      }
    });
  }


  getApoFile(startDate: string) {
    this.uploadApoService.getApoLogisticsData(startDate).subscribe((res: any) => {
      const rows = flattenOrdersFromLogisticsResponse(res);
      this.cameraHeatmap = extractCameraHeatmapFromLogisticsResponse(res);
      const enriched = rows.map((item: any) => ({
        ...item,
        value_stream: item.blender?.value_stream ?? item.value_stream ?? null,
      }));
      this.apoFile = enriched;
      this.apoFiltered = enriched;
      this.onFilter();
      this.transformData();
      this.isLoading = false;
    });
  }

  onStartDateChange(newStartDate: string) {
    this.filters.dateInit.value = newStartDate;
    this.getApoFile(this.filters.dateInit.value);
  }

  private createDateWithUTCHours(source: Date, hours: number, minutes: number, seconds: number): Date {
    return new Date(Date.UTC(
      source.getUTCFullYear(),
      source.getUTCMonth(),
      source.getUTCDate(),
      hours,
      minutes,
      seconds
    ));
  }

  private getCalendarDay(date: Date): CalendarDay | undefined {
    if (!this.calendar) return undefined;
    return this.calendar.find((day: CalendarDay) => {
      const calendarDate = new Date(day.date);
      return calendarDate.getUTCDate() === date.getUTCDate() && 
             calendarDate.getUTCMonth() === date.getUTCMonth() && 
             calendarDate.getUTCFullYear() === date.getUTCFullYear();
    });
  }

  private isWorkingDay(date: Date): boolean {
    const calendarDay = this.getCalendarDay(date);
    return calendarDay ? calendarDay.type === 'laborable' : true;
  }

  private getEndHour(date: Date): number {
    const calendarDay = this.getCalendarDay(date);
    if (calendarDay && calendarDay.end_hour) {
      const [hours, minutes] = calendarDay.end_hour.split(':').map(Number);
      return hours + minutes / 60;
    }
    return 22; // Default fallback
  }

  private getStartHour(date: Date): number {
    const calendarDay = this.getCalendarDay(date);
    if (calendarDay && calendarDay.start_hour) {
      const [hours, minutes] = calendarDay.start_hour.split(':').map(Number);
      return hours + minutes / 60;
    }
    // Default fallback when no calendar data
    return 6;
  }

  private getNextWorkingDay(date: Date): Date {
    let nextDay = new Date(date);
    nextDay.setUTCDate(nextDay.getUTCDate() + 1);
    let maxIterations = 365;
    while (!this.isWorkingDay(nextDay) && maxIterations > 0) {
      nextDay.setUTCDate(nextDay.getUTCDate() + 1);
      maxIterations--;
    }
    return nextDay;
  }


  ngOnChanges() { 
    this.transformData();
  }

  transformData() {
    this.blenderTasks = [];
    this.packagingTasks = [];
    if (this.apoFile) {
      this.apoFile.forEach((task: any) => {   
        if (task.type === OrderType.Order) {
          const startDate = new Date(task.fecha_inicio_blender);
          const endDate = new Date(task.fecha_fin_blender);
          
          if (!this.isWorkingDay(startDate)) {
            return;
          }

          if (startDate.getUTCDate() === endDate.getUTCDate()) {
            task.hora_inicio_blender = startDate.getUTCHours() + startDate.getUTCMinutes() / 60 + startDate.getUTCSeconds() / 3600;
            let newTask = { ...task,
              nuevo_production_time: task.production_time,
              original_fecha_inicio_blender: task.fecha_inicio_blender,
              original_fecha_fin_blender: task.fecha_fin_blender,
            };
            this.blenderTasks.push(newTask);
          }
          else {
            const startDateTime = new Date(task.fecha_inicio_blender);
            const endHour = this.getEndHour(startDateTime);
            const currentStartHour = startDateTime.getUTCHours() + startDateTime.getUTCMinutes() / 60 + startDateTime.getUTCSeconds() / 3600;
            const firstFabTime = endHour - currentStartHour;
            
            const endOfDayDate = this.createDateWithUTCHours(startDateTime, Math.floor(endHour), (endHour % 1) * 60, 0);

            let newTask1 = { ...task,
              fecha_inicio_blender: task.fecha_inicio_blender,
              fecha_fin_blender: endOfDayDate.toISOString(),
              original_fecha_inicio_blender: task.fecha_inicio_blender,
              original_fecha_fin_blender: task.fecha_fin_blender,
              hora_inicio_blender: currentStartHour,
              nuevo_production_time: firstFabTime,
            };

            const nextWorkingDay = this.getNextWorkingDay(startDateTime);
            const nextStartHour = this.getStartHour(nextWorkingDay);
            const nextDayStartDate = this.createDateWithUTCHours(nextWorkingDay, Math.floor(nextStartHour), (nextStartHour % 1) * 60, 0);

            let newTask2 = { ...task,
              fecha_inicio_blender: nextDayStartDate.toISOString(),
              fecha_fin_blender: task.fecha_fin_blender,
              original_fecha_inicio_blender: task.fecha_inicio_blender,
              original_fecha_fin_blender: task.fecha_fin_blender,
              hora_inicio_blender: nextStartHour,
              nuevo_production_time: task.production_time - firstFabTime,
            };

            this.blenderTasks.push(newTask1);
            if (this.isWorkingDay(nextWorkingDay)) {
              this.blenderTasks.push(newTask2);
            }
          }

          const startDateQuality = new Date(task.fecha_inicio_calidad);
          const endDateQuality = new Date(task.fecha_fin_calidad);
          
          if (!this.isWorkingDay(startDateQuality)) {
            return;
          }

          if (startDateQuality.getUTCDate() === endDateQuality.getUTCDate()) {
            task.hora_inicio_calidad = startDateQuality.getUTCHours() + startDateQuality.getUTCMinutes() / 60 + startDateQuality.getUTCSeconds() / 3600;
            let newTask = { ...task,
              type: OrderType.Quality,
              nuevo_quality_time: task.quality_time,
            };
            this.blenderTasks.push(newTask);
          }
          else {
            const startDateTime = new Date(task.fecha_inicio_calidad);
            const endHour = this.getEndHour(startDateTime);
            const currentStartHour = startDateTime.getUTCHours() + startDateTime.getUTCMinutes() / 60 + startDateTime.getUTCSeconds() / 3600;
            const firstFabTime = endHour - currentStartHour;
            
            const endOfDayDate = this.createDateWithUTCHours(startDateTime, Math.floor(endHour), (endHour % 1) * 60, 0);

            let newTask1 = { ...task,
              type: OrderType.Quality,
              nueva_fecha_inicio_calidad: task.fecha_inicio_calidad,
              nueva_fecha_fin_calidad: endOfDayDate.toISOString(),
              hora_inicio_calidad: currentStartHour,
              nuevo_quality_time: firstFabTime,
            };

            const nextWorkingDay = this.getNextWorkingDay(startDateTime);
            const nextStartHour = this.getStartHour(nextWorkingDay);
            const nextDayStartDate = this.createDateWithUTCHours(nextWorkingDay, Math.floor(nextStartHour), (nextStartHour % 1) * 60, 0);

            let newTask2 = { ...task,
              type: OrderType.Quality,
              nueva_fecha_inicio_calidad: nextDayStartDate.toISOString(),
              nueva_fecha_fin_calidad: task.fecha_fin_calidad,
              hora_inicio_calidad: nextStartHour,
              nuevo_quality_time: task.quality_time - firstFabTime,
            };
            this.blenderTasks.push(newTask1);
            if (this.isWorkingDay(nextWorkingDay)) {
              this.blenderTasks.push(newTask2);
            }
          }

          task.envasadora_orders.forEach((order: any) => {
            const startDatePackaging = new Date(order.fecha_inicio_envasadora);
            const endDatePackaging = new Date(order.fecha_fin_envasadora);
            
            if (!this.isWorkingDay(startDatePackaging)) {
              return;
            }

            if (startDatePackaging.getUTCDate() === endDatePackaging.getUTCDate()) {
              order.hora_inicio_envasadora = startDatePackaging.getUTCHours() + startDatePackaging.getUTCMinutes() / 60 + startDatePackaging.getUTCSeconds() / 3600;
              let newTask = { ...order,
                ...task,
                id: task.id,
                name: order.name,
                envasado_estado: order.envasado_estado,
                nuevo_packaging_time: order.packaging_time,
                original_fecha_inicio_envasadora: order.fecha_inicio_envasadora,
                original_fecha_fin_envasadora: order.fecha_fin_envasadora,
              }
              this.packagingTasks.push(newTask);
  
            } else {
              const startDateTime = new Date(order.fecha_inicio_envasadora);
              const endHour = this.getEndHour(startDateTime);
              const currentStartHour = startDateTime.getUTCHours() + startDateTime.getUTCMinutes() / 60 + startDateTime.getUTCSeconds() / 3600;
              const firstEnvTime = endHour - currentStartHour;
              
              const endOfDayDate = this.createDateWithUTCHours(startDateTime, Math.floor(endHour), (endHour % 1) * 60, 0);

              let newTask1 = { ...order,
                ...task,
                id: task.id,
                name: order.name,
                envasado_estado: order.envasado_estado,
                nueva_fecha_inicio_envasadora: order.fecha_inicio_envasadora,
                nueva_fecha_fin_envasadora: endOfDayDate.toISOString(),
                original_fecha_inicio_envasadora: order.fecha_inicio_envasadora,
                original_fecha_fin_envasadora: order.fecha_fin_envasadora,
                hora_inicio_envasadora: currentStartHour,
                nuevo_packaging_time: firstEnvTime,
              };
 
              const nextWorkingDay = this.getNextWorkingDay(startDateTime);
              const nextStartHour = this.getStartHour(nextWorkingDay);
              const nextDayStartDate = this.createDateWithUTCHours(nextWorkingDay, Math.floor(nextStartHour), (nextStartHour % 1) * 60, 0);

              let newTask2 = { ...order,
                ...task,
                id: task.id,
                name: order.name,
                envasado_estado: order.envasado_estado,
                nueva_fecha_inicio_envasadora: nextDayStartDate.toISOString(),
                nueva_fecha_fin_envasadora: order.fecha_fin_envasadora,
                original_fecha_inicio_envasadora: order.fecha_inicio_envasadora,
                original_fecha_fin_envasadora: order.fecha_fin_envasadora,
                hora_inicio_envasadora: nextStartHour,
                nuevo_packaging_time: order.packaging_time - firstEnvTime,
              };
  
              this.packagingTasks.push(newTask1);
              if (this.isWorkingDay(nextWorkingDay)) {
                this.packagingTasks.push(newTask2);
              }
            }
          });
          
        } else if (task.type === OrderType.Cleaning) {
          const startDateCleaning = new Date(task.fecha_inicio_blender);
          const endDateCleaning = new Date(task.fecha_fin_blender);
          
          if (!this.isWorkingDay(startDateCleaning)) {
            return;
          }

          if (startDateCleaning.getUTCDate() === endDateCleaning.getUTCDate()) {
            task.hora_inicio = startDateCleaning.getUTCHours() + startDateCleaning.getUTCMinutes() / 60 + startDateCleaning.getUTCSeconds() / 3600;
            this.blenderTasks.push({ ...task,
              original_fecha_inicio_blender: task.fecha_inicio_blender,
              original_fecha_fin_blender: task.fecha_fin_blender,
            });
          } else {
            const startDateTime = new Date(task.fecha_inicio_blender);
            const endHour = this.getEndHour(startDateTime);
            const currentStartHour = startDateTime.getUTCHours() + startDateTime.getUTCMinutes() / 60 + startDateTime.getUTCSeconds() / 3600;
            const firstFabTime = (endHour - currentStartHour) * 60;

            const endOfDayDate = this.createDateWithUTCHours(startDateTime, Math.floor(endHour), (endHour % 1) * 60, 0);

            let newTask1 = { ...task,
              fecha_inicio_blender: task.fecha_inicio_blender,
              fecha_fin_blender: endOfDayDate.toISOString(),
              original_fecha_inicio_blender: task.fecha_inicio_blender,
              original_fecha_fin_blender: task.fecha_fin_blender,
              duration: firstFabTime,
            };

            const nextWorkingDay = this.getNextWorkingDay(startDateTime);
            const nextStartHour = this.getStartHour(nextWorkingDay);
            const nextDayStartDate = this.createDateWithUTCHours(nextWorkingDay, Math.floor(nextStartHour), (nextStartHour % 1) * 60, 0);

            let newTask2 = { ...task,
              fecha_inicio_blender: nextDayStartDate.toISOString(),
              fecha_fin_blender: task.fecha_fin_blender,
              original_fecha_inicio_blender: task.fecha_inicio_blender,
              original_fecha_fin_blender: task.fecha_fin_blender,
              duration: task.duration - firstFabTime,
            };

            this.blenderTasks.push(newTask1);
            if (this.isWorkingDay(nextWorkingDay)) {
              this.blenderTasks.push(newTask2);
            }
          }
        }
      });
    }
    this.getFineTunningData(this.date);
  }

  getFineTunningData(date: Date) {
    if (this.apoFile) {
      if (!this.isWorkingDay(date)) {
        this.filteredBlenderTasks = [];
        this.filteredPackagingTasks = [];
      } else {
        this.filteredBlenderTasks = this.blenderTasks.filter((task: any) => {
          if (task.type === 'calidad') {
            const taskDate = new Date(task.nueva_fecha_inicio_calidad ? task.nueva_fecha_inicio_calidad : task.fecha_inicio_calidad);
            return taskDate.getDate() === date.getDate() && taskDate.getMonth() === date.getMonth() && taskDate.getFullYear() === date.getFullYear();
          }
          const taskDate = new Date(task.fecha_inicio_blender ? task.fecha_inicio_blender : task.fecha_inicio);
          return taskDate.getDate() === date.getDate() && taskDate.getMonth() === date.getMonth() && taskDate.getFullYear() === date.getFullYear();
        });
        this.filteredPackagingTasks = this.packagingTasks.filter((task: any) => {
          const taskDate = new Date(task.nueva_fecha_inicio_envasadora ? task.nueva_fecha_inicio_envasadora : task.fecha_inicio_envasadora);
          return taskDate.getDate() === date.getDate() && taskDate.getMonth() === date.getMonth() && taskDate.getFullYear() === date.getFullYear();
        });
      }
    }
    this.cameraHeatmapSegments = buildCameraHeatmapSegments(
      date,
      this.cameraHeatmap,
      (d) => this.isWorkingDay(d)
    );
  }

  get envasadoraOrders(): FormArray {
    return this.form.get('envasadora_orders') as FormArray;
  }

  openModal(item: any) {
    if(item.type === OrderType.Order) {
      const defaultValues = {
        id: '',
        name: '',
        weight: null,
        observations: '',
        production_time: '',
        quality_time: '',
        packaging_time: '',
        total_time: '',
        product_nature: '',
        last_day: '',
        loss_percent: '',
        load_kanbanize: false,
        fecha_inicio_blender: '',
        fecha_fin_blender: '',
        fecha_inicio_preparacion: '',
        fecha_fin_preparacion: '',
        fecha_inicio_calidad: '',
        fecha_fin_calidad: '',
        oee_blender: '',
        oee_calidad: '',
        product: {
          description: '',
          id: '',
          blender: '',
          production_time: '',
          quality_time: '',
          start_time: '',
          product_nature: '',
          active: '',
        },
        material: {
          description: '',
          fert: '',
          weight: '',
          active: '',
          packaging: {
            id: '',
            description: '',
            active: '',
            time: '',
          },
        },
        packaging: null,
        envasadora_orders: [],  
        type: '',
        is_planned: false,
        fecha_carga: '',
      };
      let envasadoraOrdersArray = this.form.get('envasadora_orders') as FormArray;
      envasadoraOrdersArray.clear();
      let orders = item.envasadora_orders;
      orders.forEach((order: any) => {
        order.fecha_inicio_envasadora = this.formatISODate(order.fecha_inicio_envasadora);
        order.fecha_fin_envasadora = this.formatISODate(order.fecha_fin_envasadora);
        envasadoraOrdersArray.push(this.formBuilder.group(order));
      });
      const mergedItem = { ...item};
      mergedItem.fecha_inicio_blender = this.formatISODate( mergedItem.fecha_inicio_blender);
      mergedItem.fecha_fin_blender = this.formatISODate(mergedItem.fecha_fin_blender);
      mergedItem.fecha_inicio_preparacion = this.formatISODate(mergedItem.fecha_inicio_preparacion);
      mergedItem.fecha_fin_preparacion = this.formatISODate(mergedItem.fecha_fin_preparacion);
      mergedItem.fecha_inicio_calidad = this.formatISODate(mergedItem.fecha_inicio_calidad);
      mergedItem.fecha_fin_calidad = this.formatISODate(mergedItem.fecha_fin_calidad);
      mergedItem.last_day = this.formatISODate(mergedItem.last_day);
      mergedItem.fecha_carga = this.formatISODate(mergedItem.fecha_carga);
      
      this.form.patchValue(mergedItem);
      
    }
    if (item.type === OrderType.Cleaning) {
      item.fecha_inicio_blender = this.formatISODate(item.fecha_inicio_blender);
      item.fecha_fin_blender = this.formatISODate(item.fecha_fin_blender);
      this.cleaningForm.patchValue(item);
    }
    this.isModalActive = true;
    
  }

  formatISODate(dateISO: any): any {
    const date = new Date(dateISO);
    return `${date.getUTCFullYear()}-${this.addZero(date.getUTCMonth() + 1)}-${this.addZero(date.getUTCDate())}T${this.addZero(date.getUTCHours())}:${this.addZero(date.getUTCMinutes())}`;
  }

  addZero(number: any) {
    if (number < 10) {
      return `0${number}`;
    } else {
      return number;
    }
     
  }

  openDeleteModal() {
    this.isModalDeleteConfirm = true;
  }

  onSave() {
    this.isLoading = true;
    if(this.form.value.type === OrderType.Order) {
      if (this.form.invalid) {
        Object.keys(this.form.controls).forEach((controlName) => {
          const control = this.form.get(controlName);
          if (control?.invalid) {
          }
        });
      }
        const uploadData = {
          ...this.form.getRawValue(), 
        };
    
        const dateFields = [
          'fecha_inicio_blender', 'fecha_fin_blender',
          'fecha_inicio_preparacion', 'fecha_fin_preparacion',
          'fecha_inicio_calidad', 'fecha_fin_calidad',
          'last_day', 'fecha_carga',
        ];


        const packagingDateFields = [
          'fecha_inicio_envasadora', 'fecha_fin_envasadora'
        ];
    
        dateFields.forEach(field => {
          if (uploadData[field]) {
            uploadData[field] = new Date(uploadData[field] + 'Z').toISOString();
          }
        });
        uploadData.envasadora_orders.forEach((order: any) => {
          packagingDateFields.forEach(field => {
            if (order[field]) {
              order[field] = new Date(order[field] + 'Z').toISOString();
            }

          });
          delete order.hora_inicio_envasadora;
        });
    
        this.uploadApoService.editApoFile(uploadData.id, uploadData).subscribe({
          next: () => {
            this.getApoFile(this.filters.dateInit.value);
            this.isModalActive = false;
            this.isLoading = false;
          },
          error: () => {
            this.isLoading = false;
            this.hasError = true;
          },
        });
      
    }
    if(this.cleaningForm.value.type === OrderType.Cleaning) {
      if (this.cleaningForm.valid) {
        const uploadData = {
          ...this.cleaningForm.getRawValue(), 
        };
    
        const dateFields = [
          'fecha_inicio_blender', 'fecha_fin_blender','fecha_carga'
        ];

    
        dateFields.forEach(field => {
          if (uploadData[field]) {
            uploadData[field] = new Date(uploadData[field] + 'Z').toISOString();
          }
        });
    
        this.uploadApoService.editApoFile(uploadData.id, uploadData).subscribe({
          next: () => {
            this.getApoFile(this.filters.dateInit.value);
            this.isModalActive = false;
            this.isLoading = false;
          },  
          error: () => {
            this.isLoading = false;
            this.hasError = true;
          },
        });
      }
    }
    
  }

  getBlenders() {
    this.blendersSvc.getBlenders().subscribe((res: any) => {
      this.blenders = res.sort((a: any, b: any) => a.id - b.id);
    });
  }

  getPackagingMachines() {
    this.packagingMachinesSvc.getPackagingMachines().subscribe((res: any) => {
      this.packagingMachines = res.sort((a: any, b: any) => String(a.id).localeCompare(String(b.id), undefined, { numeric: true }));
    });
  }

  onDateChange(date: Date) {
    this.date = date;
    this.getFineTunningData(this.date);
  }

  onDateIncrement(increment: number) {
    const oldMonth = this.date.getMonth();
    const newDate = new Date(this.date);
    newDate.setDate(newDate.getDate() + increment);
    this.date = newDate;
    this.getFineTunningData(this.date);
    
    // Reload calendar if month changed
    if (oldMonth !== newDate.getMonth()) {
      this.loadCalendar();
    }
  }

  resetFilters(keysSelected: any) {
    for (const key of Object.keys(this.filters)) {
      if (!keysSelected.includes(key)) {
        if (key === 'load_kanbanize') {
          this.filters[key] = false;
        } else {
          this.filters[key] = '';
        }

      }
    }
  }

  onFilter() {
    this.apoFiltered = this.apoFile.filter((obj: any) => {
      return Object.keys(this.filters).map(f => {
        return !this.filters[f].value ? true : this.filters[f].filter(obj);
      }).filter(f => f).length === Object.keys(this.filters).length;
    });

  }



  closeModal() {
    this.form.reset();
    this.cleaningForm.reset();
    let envasadoraOrdersArray = this.form.get('envasadora_orders') as FormArray;
    envasadoraOrdersArray.clear();
    this.hasError = false;
    this.getApoFile(this.filters.dateInit.value);
    this.isModalActive = false;
  }

  closeNotification() {
    this.hasError = false;
  }

  delete(item?: any) {
    this.isLoading = true
    this.uploadApoService.deleteApoFile(item.id, item.type).subscribe({
      next: () => {
        this.getApoFile(this.filters.dateInit.value);
        this.isLoading = false;
        this.toastr.success(`La orden ha sido eliminada correctamente`);
      },
      error: () => {
        this.isLoading = false
      }
    });
  }

  deleteAllOrders() {
    this.isLoading = true;
    // this.uploadApoService.deleteAllOrders().subscribe({
    //   next: () => {
    //     this.getApoFile();
    //     this.isLoading = false;
    //     this.isModalDeleteConfirm = false;
    //     this.toastr.success(`Las órdenes han sido eliminadas correctamente`);
    //   },
    //   error: () => {
    //     this.isLoading = false
    //   }
    // });
  }

  get blendersForVS(): any[] {
    return (this.blenders ?? []).filter((b: any) => b.value_stream === this.selectedValueStream);
  }

  get packagingMachinesForVS(): any[] {
    return (this.packagingMachines ?? []).filter((p: any) => p.value_stream === this.selectedValueStream);
  }

  get blendersForTimeline(): any[] {
    return this.selectedBlenders.length > 0 ? this.selectedBlenders : this.blendersForVS;
  }

  get packagingMachinesForTimeline(): any[] {
    return this.selectedPackagingMachines.length > 0 ? this.selectedPackagingMachines : this.packagingMachinesForVS;
  }

  compareBlenders(blender1: any, blender2: any): boolean {
    return blender1 && blender2 ? blender1.id === blender2.id : blender1 === blender2;
  }


}
